/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to UK-facing transparency language.
 * The page should emphasize notice quality, clarity of rights, and disciplined handling
 * for UK visitors without drifting into vague or inflated legal claims.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { BookText, Eye, Scale } from "lucide-react";

const cards = [
  {
    title: "Transparency first",
    body: "UK visitors should be able to understand in plain language what information may be handled, why it may be used, and how they can request review or correction where UK rights expectations apply.",
    icon: Eye,
  },
  {
    title: "Rights-aware handling",
    body: "This page recognises that UK users may expect access, rectification, erasure, restriction, objection, portability, and complaint pathways where the UK GDPR or related rules apply in context.",
    icon: Scale,
  },
  {
    title: "Plain customer wording",
    body: "The legal position is written to be concise, intelligible, and visibly connected to support, privacy, and operational review rather than hidden behind abstract compliance language.",
    icon: BookText,
  },
];

const sections = [
  {
    id: "scope",
    title: "Who this UK notice is for",
    intro:
      "This page is intended for visitors, customers, or support requesters in the United Kingdom who expect the public legal position to be explained with UK-facing transparency and rights language.",
    paragraphs: [
      "Command Secrets is not presented as a United Kingdom-only service, but the public privacy and support posture recognises that UK visitors may reasonably expect a clear explanation of what data is handled, why it is used, how long it may be kept, and which request path is available when problems arise.",
      "This page should be read alongside the general privacy notice, support page, disclaimer, cancellation terms, and security page. It is a public explanation rather than a substitute for every narrower term, contract, or case-specific notice that may apply in a particular operational context.",
      "Where the product, pricing, integrations, support path, or data flows materially change, the wording on this page may also change so the public explanation remains current and intelligible.",
    ],
  },
  {
    id: "transparency",
    title: "What the service aims to explain clearly",
    intro:
      "UK-facing legal wording should not force visitors to guess what the service is doing with their information.",
    paragraphs: [
      "Command Secrets aims to explain the categories of personal data that may be collected, the operational reasons the data may be used, the kinds of third parties that may help process it, the likely retention context, and the practical route for support, privacy, billing, or correction requests.",
      "The service may use data for account access, subscription handling, report generation, delivery confirmation, technical support, fraud prevention, release management, security monitoring, and lawful compliance. Those purposes are framed as operational needs rather than as a blanket permission to use data however the business wishes.",
      "The platform also aims to keep its public wording concise and readable so users can understand the handling position without needing specialist legal interpretation before deciding whether to continue.",
    ],
  },
  {
    id: "rights",
    title: "UK-style rights and request expectations",
    intro:
      "Where the UK GDPR or related UK data-protection expectations apply, individuals may expect specific rights and a visible route for raising them.",
    paragraphs: [
      "Depending on the facts of the relationship, UK users may seek access, rectification, erasure, restriction, objection, portability, or clarification about how their personal information is being handled. The availability and scope of any given right can depend on law, context, technical feasibility, and the reason the data is being retained.",
      "Command Secrets may request enough information to verify identity, narrow the request, or confirm that the requester is entitled to act for the relevant account. This is intended to prevent protected data from being disclosed or changed for the wrong person.",
      "A request may be delayed, narrowed, or refused where identity cannot be verified, where lawful retention remains necessary, where another person's rights would be affected, or where the request exceeds what the applicable legal framework requires in context.",
    ],
  },
  {
    id: "vendors-and-transfers",
    title: "Vendors, hosting, and cross-border handling",
    intro:
      "UK-facing transparency requires acknowledging that digital services often rely on processors and infrastructure outside the visitor's own region.",
    paragraphs: [
      "Command Secrets may rely on hosting providers, payment processors, technical infrastructure vendors, logging tools, or specialist contractors to operate the website, protect the service surface, or deliver paid outputs.",
      "Because digital operations can involve cross-border vendors, personal data may be stored or processed outside the United Kingdom. The service aims to use reasonable operational, technical, and contractual safeguards suited to the business context rather than presenting uncontrolled data sharing as normal.",
      "The security page provides additional context about restricted internal access, password protection, abuse monitoring, report protection, and the expectation that customer outputs should not be casually exposed or redistributed.",
    ],
  },
  {
    id: "communications",
    title: "Support, notifications, and communication clarity",
    intro:
      "UK users should be able to distinguish operational messages from discretionary promotional communications.",
    paragraphs: [
      "Operational messages may include account notices, support replies, report delivery notices, billing clarifications, release-status updates, security-related warnings, or legally required communications connected to the service relationship.",
      "If promotional or marketing communication channels are introduced, Command Secrets aims to make the communication purpose reasonably clear and to provide appropriate channel-specific opt-out handling where relevant.",
      "Questions about communication preferences, delivery failures, privacy requests, or release-review concerns should be routed through the support page so they can be reviewed through a controlled manual workflow.",
    ],
  },
  {
    id: "complaints-and-contact",
    title: "How UK visitors should raise concerns or complaints",
    intro:
      "A rights-aware notice should point users toward an actual review route rather than leaving the next step ambiguous.",
    paragraphs: [
      "UK-facing privacy, support, billing, correction, and account questions should be raised through the support page so the issue can be logged and triaged with enough context to review it responsibly.",
      "Customers should include the account email, affected plan or order reference if available, the relevant page or screen, and a concise explanation of the issue or request. This is particularly important for privacy and correction matters.",
      "If a UK visitor believes the service has not handled a privacy or rights-related concern appropriately, the matter can first be raised through the support path so Command Secrets has an opportunity to review, clarify, correct, or explain the position before the issue is taken further.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "Read the broader privacy page that connects UK-facing transparency to the service's global handling position.",
  },
  {
    label: "Singapore legal notice",
    href: "/legal/singapore",
    description: "Compare the Singapore-facing PDPA-first explanation used for local users and residents.",
  },
  {
    label: "Support",
    href: "/support",
    description: "Use the support intake flow for privacy, billing, report delivery, technical issues, and release-review concerns.",
  },
  {
    label: "Security",
    href: "/security",
    description: "Review the public-facing access, resilience, and anti-abuse posture for the site and protected outputs.",
  },
];

export default function UkLegal() {
  return (
    <PolicyLayout
      eyebrow="UK legal notice"
      title="United Kingdom transparency and rights wording for UK visitors and requesters."
      intro="This page explains how Command Secrets frames notice quality, privacy expectations, support routing, and rights-aware handling for United Kingdom visitors in clear public language."
      highlight="Say what is collected, why it matters, who may help process it, and how a user can challenge or correct it."
      cards={cards}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
